import React, { useState } from 'react';
import {
  ArrowLeft,
  LogOut,
  UserRound,
  Mail,
  Phone,
  Crown,
  Smartphone,
  ChevronRight,
  Calendar,
  RefreshCw,
  Plus,
  Check,
  BadgeAlert,
} from 'lucide-react';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import florLogo from '../assets/florDaisiesSF.png';
import WhatsappVerificationModal from './WhatsappVerificationModal';

// Tiendas — fuente: linktree-daisies (descarga.daisies.mx)
const APP_STORE_URL = 'https://apps.apple.com/mx/app/daisies/id1608605255?l=en-GB';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=health.neuron.daisies';

const formatDate = (raw) => {
  if (!raw) return '';
  try {
    const d = new Date(`${String(raw).split(' ')[0]}T12:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
};

// Abre la app de Daisies con un puente de sesión (jwt) para no re-loguear.
// Mismo patrón que "Completa tu Expediente" del dashboard.
const openDaisiesApp = async () => {
  try {
    const { auth } = await import('../services/FirebaseService');
    const fbUser = auth.currentUser;
    if (fbUser) {
      const jwt = await fbUser.getIdToken(true);
      window.open(`https://app.daisies.mx/authPage?jwt=${encodeURIComponent(jwt)}`, '_blank');
      return;
    }
  } catch {
    /* fallthrough */
  }
  window.open('https://app.daisies.mx', '_blank');
};

const ProfilePage = ({ user, onBack, onLogout, onSelectMembership, onUserUpdate }) => {
  const [showVerify, setShowVerify] = useState(false);

  // ── Estado vacío: la sesión aún no aterriza (refresh directo a /perfil) ──
  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand-tint">
          <UserRound size={26} className="text-brand" aria-hidden="true" />
        </span>
        <h1 className="text-xl font-bold text-brand-deep">Inicia sesión para ver tu perfil</h1>
        <p className="mt-2 text-sm text-text-body">Aquí encuentras tu información y tu membresía.</p>
        <button
          onClick={onBack}
          className="press-scale mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-dark"
        >
          Volver al inicio
        </button>
      </div>
    );
  }

  const apellido = (user.apellido || '').trim();
  const hasApellido = apellido && !/^sin\s+apellido$/i.test(apellido);
  const fullName = user.nombre
    ? (hasApellido ? `${user.nombre} ${apellido}` : user.nombre)
    : (user.email || 'Tu perfil');

  const phone = user.telefono || user.phone || '';
  const phoneVerified = user.phoneVerified === true;
  const userId = user.daisUserId || user.userId;
  const authToken = user.authToken;
  const canVerify = !!(userId && authToken);

  const hasMembership =
    user.membership_active === 'active' ||
    user.membership_active === true ||
    user.membership?.state === 'active' ||
    !!user.membership?.name;
  const m = user.membership || {};
  const minutes = Number.isFinite(user.balance) ? user.balance : null;
  const planName = m.name || 'Membresía Daisies';
  const purchaseDate = formatDate(m.date_paid || m.start_date || m.date_active);
  const minutesRenewDate = formatDate(m.date_renovation_minutes);

  const infoRows = [
    { Icon: UserRound, label: 'Nombre', value: fullName },
    { Icon: Mail, label: 'Correo', value: user.email || 'Sin registrar' },
    {
      Icon: Phone,
      label: 'Teléfono',
      value: phone || 'Sin registrar',
      badge: phone
        ? (phoneVerified
            ? { text: 'Verificado', cls: 'bg-mint-pill text-mint-deep', Icon: Check }
            : { text: 'Sin verificar', cls: 'bg-amber-pill/30 text-amber-deep', Icon: BadgeAlert })
        : null,
    },
  ];

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* ─── Top bar ─── */}
      <header className="mb-8 flex items-center justify-between">
        <button
          onClick={onBack}
          className="press-scale inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold text-brand-deep transition-colors hover:bg-brand-tint"
        >
          <ArrowLeft size={18} aria-hidden="true" />
          Volver
        </button>
        <button
          onClick={onLogout}
          aria-label="Cerrar sesión"
          className="press-scale inline-flex items-center gap-2 rounded-full border-[1.5px] border-brand px-5 py-2.5 text-sm font-semibold text-brand transition-colors hover:bg-brand-tint"
        >
          <LogOut size={16} aria-hidden="true" />
          <span className="hidden md:inline">Cerrar sesión</span>
        </button>
      </header>

      {/* ─── Identidad ─── */}
      <section className="animate-fadeInUp mb-8 flex items-center gap-4">
        {user.avatar ? (
          <img
            src={user.avatar}
            alt={fullName}
            className="h-16 w-16 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-brand-tint">
            <img src={florLogo} alt="" aria-hidden="true" className="h-9 w-auto" />
          </span>
        )}
        <div className="min-w-0">
          <p className="text-[12px] font-semibold text-brand-deep/60">Tu perfil</p>
          <h1 className="truncate text-[clamp(1.4rem,3vw,1.8rem)] font-bold leading-tight tracking-[-0.02em] text-brand-deep">
            {fullName}
          </h1>
          {user.email && (
            <p className="truncate text-sm text-text-mute">{user.email}</p>
          )}
        </div>
      </section>

      {/* ─── Completar perfil: agregar / verificar teléfono ─── */}
      {!phoneVerified && canVerify && (
        <section className="animate-fadeInUp mb-8">
          <div className="rounded-card bg-brand-tint/60 p-6">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#25D366]/15 text-[#25D366]">
                <Smartphone size={20} aria-hidden="true" />
              </span>
              <div className="flex-1">
                <p className="font-semibold text-brand-deep">
                  {phone ? 'Verifica tu teléfono' : 'Agrega tu teléfono'}
                </p>
                <p className="mt-1 text-sm text-text-body">
                  Lo usamos para enviarte el enlace de tu consulta y recordatorios por WhatsApp.
                </p>
                <button
                  onClick={() => setShowVerify(true)}
                  className="press-scale mt-4 inline-flex items-center gap-1.5 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-dark"
                >
                  {phone ? 'Verificar por WhatsApp' : 'Agregar y verificar'}
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── Tu información ─── */}
      <section className="animate-fadeInUp-1 mb-8">
        <h2 className="mb-3 text-base font-semibold text-brand-deep">Tu información</h2>
        <div className="overflow-hidden rounded-card bg-surface-card">
          {infoRows.map((row, i) => (
            <div
              key={row.label}
              className={`flex items-center gap-4 px-5 py-4 ${
                i > 0 ? 'border-t border-brand-100/70' : ''
              }`}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-tint text-brand">
                <row.Icon size={18} aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-medium text-text-mute">{row.label}</p>
                <p className="truncate text-[15px] font-semibold text-ink">{row.value}</p>
              </div>
              {row.badge && (
                <span
                  className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-semibold ${row.badge.cls}`}
                >
                  <row.badge.Icon size={13} aria-hidden="true" />
                  {row.badge.text}
                </span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ─── Tu membresía ─── */}
      <section className="animate-fadeInUp-2 mb-8">
        <h2 className="mb-3 text-base font-semibold text-brand-deep">Tu membresía</h2>
        {hasMembership ? (
          <div className="relative overflow-hidden rounded-card bg-brand p-6 text-white">
            <div
              aria-hidden="true"
              className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10 blur-2xl"
            />
            <div className="relative z-10">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-[12px] font-bold uppercase tracking-wide">
                <Crown size={13} aria-hidden="true" />
                Activa
              </div>
              <p className="text-lg font-bold leading-tight">{planName}</p>
              {minutes !== null && (
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="text-4xl font-bold leading-none tracking-tight">{minutes}</span>
                  <span className="text-base font-medium text-white/80">min disponibles</span>
                </div>
              )}

              {(purchaseDate || minutesRenewDate) && (
                <div className="mt-5 space-y-2 border-t border-white/20 pt-4">
                  {purchaseDate && (
                    <p className="flex items-center gap-2 text-[13px] text-white/85">
                      <Calendar size={14} className="shrink-0 text-white/70" aria-hidden="true" />
                      Comprada el {purchaseDate}
                    </p>
                  )}
                  {minutesRenewDate && (
                    <p className="flex items-center gap-2 text-[13px] text-white/85">
                      <RefreshCw size={14} className="shrink-0 text-white/70" aria-hidden="true" />
                      Tus minutos se renuevan el {minutesRenewDate}
                    </p>
                  )}
                </div>
              )}

              <button
                onClick={openDaisiesApp}
                className="press-scale mt-5 inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-brand-dark transition-colors hover:bg-brand-tint"
              >
                <Plus size={16} aria-hidden="true" />
                Comprar más minutos
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-card bg-surface-card p-6">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-tint text-brand">
                <Crown size={20} aria-hidden="true" />
              </span>
              <div className="flex-1">
                <p className="font-semibold text-brand-deep">Aún no tienes una membresía activa</p>
                <p className="mt-1 text-sm text-text-body">
                  Con una membresía recibes minutos al mes para repartir entre todas tus doctoras.
                </p>
                {onSelectMembership && (
                  <button
                    onClick={() => onSelectMembership({ name: 'Membresía Integral', membershipId: 21 })}
                    className="press-scale mt-4 inline-flex items-center gap-1.5 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-dark"
                  >
                    Ver membresía
                    <ChevronRight size={16} aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ─── Gestiona desde la app ─── */}
      <section className="animate-fadeInUp-3">
        <div className="rounded-card bg-surface-card p-6">
          <div className="mb-4 flex items-start gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-pill/25 text-amber-deep">
              <Smartphone size={20} aria-hidden="true" />
            </span>
            <div className="flex-1">
              <p className="font-semibold text-brand-deep">¿Necesitas cambiar tus datos?</p>
              <p className="mt-1 text-sm text-text-body">
                Por ahora, actualizar tu información y gestionar tu membresía se hace desde la app
                de Daisies. Ahí tienes todo tu expediente en un solo lugar.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2.5 sm:flex-row">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="press-scale flex flex-1 items-center justify-center gap-2.5 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              <FaApple size={18} aria-hidden="true" />
              App Store
            </a>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="press-scale flex flex-1 items-center justify-center gap-2.5 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              <FaGooglePlay size={16} aria-hidden="true" />
              Google Play
            </a>
          </div>
        </div>
      </section>

      {/* ─── Modal verificación WhatsApp (reusa el flujo del gate de agenda) ─── */}
      {showVerify && (
        <WhatsappVerificationModal
          userId={userId}
          authToken={authToken}
          phone={phone}
          sendTitle={phone ? 'Verifica tu WhatsApp' : 'Agrega tu teléfono'}
          sendSubtitle="Lo usamos para enviarte el enlace de tu consulta y recordatorios por WhatsApp."
          onPhoneChanged={(newPhone) => onUserUpdate?.({ telefono: newPhone, phoneVerified: false })}
          onVerified={(res) => {
            onUserUpdate?.({ phoneVerified: true, ...(res?.stepState ? { stepState: res.stepState } : {}) });
            setShowVerify(false);
          }}
          onCancel={() => setShowVerify(false)}
        />
      )}
    </div>
  );
};

export default ProfilePage;
